// Member pricing (M8): the one place the rule lives. The server's invoice and estimate lines, the
// iPad's optimistic line and estimate builder, and "Apply member prices" all call it, so a line
// priced offline is priced the same when it reaches the server.
//
// A member's line from the price book is priced at the item's member price when it has one (it wins
// over the plan's percentage, as ServiceTitan does it), otherwise at the book price less the plan's
// discount, rounded half up to the cent. Only work and parts: service, material, equipment and labour
// lines; never fees, discounts or adjustments, a custom line (no book price) or a membership's own
// sale line. A discount item's own member price is the one exception (the lead's ruling, 20 Oct:
// ServiceTitan's −30 against −25 for members, as Brook's members get): it applies, the plan's
// percentage never does. With two plans at the address the lower price wins. Never above the book price.

export const MEMBER_PRICED_TYPES = ['service', 'material', 'equipment', 'labour'] as const;

/** An active membership at the job's address, as pricing needs it. */
export interface MemberPlan {
  membershipId: string;
  planName: string;
  /** The plan's member discount, "10" or "12.5" (percent). */
  discountPct: string;
  /** The price-book item that sells the plan: its own line is never member priced. */
  /** null on a plan imported bare (no item sells it yet). */
  saleItemId: string | null;
}

export interface MemberPriced {
  /** The price for this line: the member price, or the book price when none applies. */
  priceCents: number;
  /** The membership whose pricing applied; null when none did. */
  memberPriceOf: string | null;
  planName: string | null;
  /** The regular book price, kept when a member price applied ("You saved $X"). */
  listPriceCents: number | null;
}

/** The book price less a discount in percent (up to 2 decimals), rounded half up to the cent. */
export function discounted(priceCents: number, discountPct: string): number {
  const bp = Math.round(Number(discountPct) * 100); // basis points
  if (!Number.isFinite(bp) || bp <= 0) return priceCents;
  if (bp >= 10_000) return 0;
  return Math.floor((priceCents * (10_000 - bp) + 5_000) / 10_000);
}

export function memberPrice(
  item: { id: string; type: string; priceCents: number; memberPriceCents: number | null },
  plans: readonly MemberPlan[],
): MemberPriced {
  const none: MemberPriced = {
    priceCents: item.priceCents,
    memberPriceOf: null,
    planName: null,
    listPriceCents: null,
  };
  // a discount item: its own member price for a member, when it's the bigger discount; never a percentage
  const discountsMore =
    item.type === 'discount' && item.memberPriceCents !== null && item.memberPriceCents < item.priceCents;
  if (!discountsMore && !(MEMBER_PRICED_TYPES as readonly string[]).includes(item.type)) return none;
  if (!plans.length || plans.some((p) => p.saleItemId === item.id)) return none;
  let best: { cents: number; plan: MemberPlan } | null = null;
  for (const plan of plans) {
    const cents = item.memberPriceCents ?? discounted(item.priceCents, plan.discountPct);
    if (!best || cents < best.cents) best = { cents, plan };
  }
  if (!best || best.cents >= item.priceCents) return none;
  return {
    priceCents: best.cents,
    memberPriceOf: best.plan.membershipId,
    planName: best.plan.planName,
    listPriceCents: item.priceCents,
  };
}

/**
 * The book price a saved line is measured from: the member price, unless the caller kept the regular
 * price on purpose (an estimate built before the membership, sent again with its list price), in which
 * case the line stays at list and isn't member priced.
 */
export function bookFor(priced: MemberPriced, unitPriceCents: number | undefined): MemberPriced {
  if (priced.memberPriceOf && unitPriceCents !== undefined && unitPriceCents === priced.listPriceCents)
    return { priceCents: priced.listPriceCents, memberPriceOf: null, planName: null, listPriceCents: null };
  return priced;
}

/**
 * Whether "Apply member prices" may reprice a line already on the invoice: from the price list, still
 * at its book price, not yet member priced, and never a person's manual price (a reason given) or a
 * line from a signed estimate option: the customer signed that price, and the invoice keeps it (the
 * lead, 18 Oct; a new member's discount on it is an explicit discount line with a reason).
 */
export function repriceable(l: {
  pricebookItemId: string | null;
  memberPriceOf: string | null;
  overrideReason: string | null;
  sourceLineId: string | null;
  unitPriceCents: number;
  bookPriceCents: number | null;
}): boolean {
  return (
    l.pricebookItemId !== null &&
    l.memberPriceOf === null &&
    l.overrideReason === null &&
    l.sourceLineId === null &&
    l.bookPriceCents !== null &&
    l.unitPriceCents === l.bookPriceCents
  );
}
